import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { cn } from '#/lib/utils';
import { DASHBOARD_TONES } from '#/modules/infra/dashboard/helper/dashboardModules';
import { formatCount } from '#/modules/infra/dashboard/helper/dashboardChartTheme';
import { buildCardHref } from '#/modules/infra/dashboard/helper/dashboardRange';

/**
 * 狀態卡（等比自動欄，整塊底色＝狀態語意色）；只渲染 `values` 內存在的 key（無財務權限時被省略的卡不顯示、不補 0）。
 * `kind`：backlog＝目前待辦（鑽取只帶 status）；completion＝完成期間（鑽取另帶完成日期）。見 buildCardHref。
 * 沒有 `status` 的卡只顯示筆數、不可點（`hint` 為滑過說明）。
 */
const DashboardMetricCards = ({ config, cards, values, kind, range, asOfDate }) => {
  const navigate = useNavigate();
  const visible = cards.filter((card) => values?.[card.key] !== undefined);

  if (visible.length === 0) {
    return null;
  }

  return (
    <div className="grid auto-cols-fr grid-flow-col gap-1.5">
      {visible.map((card) => {
        const tone = DASHBOARD_TONES[card.tone] ?? DASHBOARD_TONES.pending;
        const className = cn(
          'flex min-w-0 flex-col items-start gap-0.5 rounded-md border px-2 py-1.5 text-left transition-colors',
          tone.tileClass,
        );
        const content = (
          <>
            <span className="w-full truncate text-xs text-muted-foreground">{card.label}</span>
            <span className="text-lg leading-tight font-semibold tabular-nums text-foreground">
              {formatCount(values[card.key])}
            </span>
          </>
        );
        if (!card.status) {
          return (
            <div key={card.key} className={cn(className, 'cursor-default')} title={card.hint ?? card.label}>
              {content}
            </div>
          );
        }
        const href = buildCardHref(config, card, { kind, range, asOfDate });
        return (
          <button
            key={card.key}
            type="button"
            className={className}
            title={`查看${config.title}：${card.label}`}
            onClick={() => navigate(href)}
          >
            {content}
          </button>
        );
      })}
    </div>
  );
};

const cardShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  status: PropTypes.string,
  overdue: PropTypes.oneOf(['plannedShip', 'unreceived', 'scheduledStart']),
  tone: PropTypes.string,
  hint: PropTypes.string,
});

DashboardMetricCards.propTypes = {
  config: PropTypes.shape({
    key: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    listRoute: PropTypes.string.isRequired,
  }).isRequired,
  cards: PropTypes.arrayOf(cardShape).isRequired,
  values: PropTypes.object,
  kind: PropTypes.oneOf(['backlog', 'completion']).isRequired,
  range: PropTypes.shape({ from: PropTypes.string, to: PropTypes.string }),
  asOfDate: PropTypes.string,
};

export default DashboardMetricCards;
